import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { trPara, trSayi } from '@/lib/sayi';
import { toast } from 'sonner';
import { Loader2, RefreshCw, UtensilsCrossed, X, Link2, Info } from 'lucide-react';

/**
 * FİX MENÜLER (kullanıcı, 2026-10-27). Fix menü POS yönetici panelinde açılır,
 * buraya çekimle gelir. Ekranın işi menüyü malzemesinin girildiği ÜCRETLİ
 * TÜKETİM kayıtlarına bağlamak ve organizasyonun kârını göstermek:
 * ciro (POS'taki fix menü satırı) − maliyet (bağlı tüketimler).
 * Kapora yalnızca bilgidir: ciro etkinlik günü fix menü satırıyla doğar.
 * Bekleyen tüketimin maliyeti bugünkü fiyatla TAHMİNDİR — "kesin" ancak bütün
 * bağlı tüketimler onaylanınca yazılır.
 */

const DURUM = { pending: 'Onay bekliyor', approved: 'Onaylı', rejected: 'Reddedildi' };
const hata = (e, v) => e?.response?.data?.detail || v;
const gunAy = (t) => (t ? t.split('-').reverse().join('.') : '—');

export default function FixMenuler() {
  const { hasPermission } = useAuth();
  const duzenler = hasPermission('pos_integration', 'edit');
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [cekiliyor, setCekiliyor] = useState(false);
  const [secili, setSecili] = useState(null);

  const yukle = useCallback(async () => {
    try {
      const { data } = await api.get('/pos/fix-menuler');
      setVeri(data);
    } catch (e) {
      toast.error(hata(e, 'Fix menüler yüklenemedi'));
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const posCek = async () => {
    setCekiliyor(true);
    try {
      const { data } = await api.post('/pos/fix-menuler/cek');
      toast.success(`${data.adet} fix menü POS'tan tazelendi`);
      await yukle();
    } catch (e) {
      toast.error(hata(e, "POS'tan çekilemedi"));
    } finally {
      setCekiliyor(false);
    }
  };

  const liste = veri?.fix_menuler || [];
  const seciliMenu = secili ? liste.find(f => f.id === secili) : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UtensilsCrossed size={18} /> Fix Menüler
        </CardTitle>
        {duzenler && (
          <Button variant="outline" size="sm" onClick={posCek} disabled={cekiliyor} data-testid="fix-menu-cek">
            {cekiliyor ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
            POS'tan yenile
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#6b6a65] flex gap-2">
          <Info size={16} className="shrink-0 mt-0.5" />
          <span>
            Fix menü POS yönetici panelinde açılır. Ciro, adisyondaki fix menü satırından gelir ve
            stoktan bir şey düşmez; malzeme ERP'de <b>Ücretli Tüketim</b> olarak girilip buradan menüye
            bağlanır. Satış ve kasada alınan kapora ancak adisyon aktarıldıktan sonra görünür.
          </span>
        </p>
        {yukleniyor ? (
          <div className="py-10 text-center text-[#6b6a65]"><Loader2 className="animate-spin inline mr-2" size={16} />Yükleniyor</div>
        ) : !liste.length ? (
          <div className="py-10 text-center text-[#6b6a65]">Henüz fix menü yok. POS yönetici panelinden açılınca burada görünür.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[#6b6a65]">
                  <th className="py-2 pr-3 font-medium">Tarih</th>
                  <th className="py-2 pr-3 font-medium">Fix Menü</th>
                  <th className="py-2 pr-3 font-medium text-right">Kişi</th>
                  <th className="py-2 pr-3 font-medium text-right">Ciro</th>
                  <th className="py-2 pr-3 font-medium text-right">Kapora</th>
                  <th className="py-2 pr-3 font-medium text-right">Maliyet</th>
                  <th className="py-2 font-medium text-right">Kâr</th>
                </tr>
              </thead>
              <tbody>
                {liste.map(f => (
                  <tr key={f.id} className="border-b hover:bg-[#f7f6f2] cursor-pointer" onClick={() => setSecili(f.id)}
                      data-testid={`fix-menu-satir-${f.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{gunAy(f.tarih)}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-[#1a1916]">{f.ad}</div>
                      <div className="text-xs text-[#6b6a65]">
                        {f.musteri_adi || '—'}
                        {f.durum === 'iptal' && <Badge variant="outline" className="ml-2 text-red-600 border-red-300">İptal</Badge>}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right">{f.satis_adet ? trSayi(f.satis_adet) : <span className="text-[#9a9890]">{f.kisi_sayisi || '—'}</span>}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{f.ciro ? trPara(f.ciro) : '—'}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{(f.kapora_kasada + f.kapora_disarida) ? trPara(f.kapora_kasada + f.kapora_disarida) : '—'}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {!f.tuketim_sayisi ? <span className="text-amber-700">Tüketim bağlı değil</span>
                        : f.maliyet == null ? `${f.tuketim_sayisi} tüketim`
                        : <>{trPara(f.maliyet)}{!f.maliyet_kesin && <span className="text-amber-700 text-xs ml-1">(tahmini)</span>}</>}
                    </td>
                    <td className={`py-2 text-right whitespace-nowrap font-medium ${f.kar < 0 ? 'text-red-600' : 'text-[#1a1916]'}`}>
                      {f.kar != null && f.tuketim_sayisi && f.ciro ? trPara(f.kar) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {seciliMenu && (
        <FixMenuDetay menu={seciliMenu} duzenler={duzenler} maliyetGorur={veri?.maliyet_gorur}
                      onKapat={() => setSecili(null)} onDegisti={yukle} />
      )}
    </Card>
  );
}

function FixMenuDetay({ menu, duzenler, maliyetGorur, onKapat, onDegisti }) {
  const [adaylar, setAdaylar] = useState([]);
  const [secim, setSecim] = useState('');
  const [mesgul, setMesgul] = useState(false);

  const adaylariYukle = useCallback(async () => {
    if (!duzenler) return;
    try {
      const { data } = await api.get('/pos/fix-menuler/tuketim-adaylari');
      setAdaylar(data);
    } catch (e) {
      toast.error(hata(e, 'Tüketimler yüklenemedi'));
    }
  }, [duzenler]);

  useEffect(() => { adaylariYukle(); }, [adaylariYukle]);

  // Menü tarihine yakın tüketimler önce: organizasyon malzemesi genellikle o gün girilir
  const secenekler = useMemo(() => {
    const t0 = menu.tarih ? new Date(menu.tarih).getTime() : 0;
    return [...adaylar]
      .sort((a, b) => Math.abs(new Date(a.tarih) - t0) - Math.abs(new Date(b.tarih) - t0))
      .map(a => ({
        value: a.id,
        label: `${gunAy(a.tarih)} · ${a.name || 'Adsız tüketim'}`,
        hint: `${DURUM[a.status] || a.status}${a.maliyet != null ? ' · ' + trPara(a.maliyet) : ''}`,
      }));
  }, [adaylar, menu.tarih]);

  const bagla = async (tuketimId, bagla = true) => {
    setMesgul(true);
    try {
      await api.post(`/pos/fix-menuler/${menu.id}/tuketim`, { tuketim_id: tuketimId, bagla });
      toast.success(bagla ? 'Tüketim bağlandı' : 'Bağ kaldırıldı');
      setSecim('');
      await Promise.all([onDegisti(), adaylariYukle()]);
    } catch (e) {
      toast.error(hata(e, 'İşlem yapılamadı'));
    } finally {
      setMesgul(false);
    }
  };

  const kaporaToplam = menu.kapora_kasada + menu.kapora_disarida;

  return (
    <Dialog open onOpenChange={(a) => !a && onKapat()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{menu.ad} · {gunAy(menu.tarih)}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[#6b6a65]">
          {menu.musteri_adi && <>{menu.musteri_adi} · </>}
          {menu.fiyat_turu === 'kisi_basi'
            ? <>Kişi başı {trPara(menu.fiyat)}{menu.kisi_sayisi ? ` × ${menu.kisi_sayisi} kişi = ${trPara(menu.planlanan_tutar)}` : ''}</>
            : <>Toplam {trPara(menu.fiyat)}</>}
          {menu.aciklama && <div className="mt-1">{menu.aciklama}</div>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Kutu baslik="Ciro" deger={menu.ciro ? trPara(menu.ciro) : '—'}
                alt={menu.satis_adet ? `${trSayi(menu.satis_adet)} kişi/adet` : 'Satış aktarılmadı'} />
          <Kutu baslik="Kapora" deger={kaporaToplam ? trPara(kaporaToplam) : '—'}
                alt={kaporaToplam ? `Kasada ${trPara(menu.kapora_kasada)} · Dışarıda ${trPara(menu.kapora_disarida)}` : ''} />
          <Kutu baslik="Maliyet" deger={menu.maliyet != null && menu.tuketim_sayisi ? trPara(menu.maliyet) : '—'}
                alt={!menu.tuketim_sayisi ? 'Tüketim bağlı değil' : menu.maliyet_kesin ? 'Kesin' : 'Tahmini (onay bekleyen var)'} />
          <Kutu baslik="Kâr" deger={menu.kar != null && menu.tuketim_sayisi && menu.ciro ? trPara(menu.kar) : '—'}
                alt={menu.kar != null && menu.ciro && menu.tuketim_sayisi ? `%${trSayi(menu.kar / menu.ciro * 100, 1)}` : ''}
                kirmizi={menu.kar < 0} />
        </div>

        <Bolum baslik="Bağlı ücretli tüketimler">
          {!menu.tuketimler.length && <div className="text-sm text-[#6b6a65]">Henüz bağlanmadı.</div>}
          {menu.tuketimler.map(t => (
            <div key={t.id} className="flex items-center gap-2 py-1.5 border-b last:border-0 text-sm">
              <div className="flex-1 min-w-0">
                <div className="truncate">{t.name || 'Adsız tüketim'}</div>
                <div className="text-xs text-[#6b6a65]">{gunAy(t.tarih)} · {DURUM[t.status] || t.status} · {t.kalem_sayisi} kalem</div>
              </div>
              {maliyetGorur && <div className="whitespace-nowrap">{trPara(t.maliyet)}</div>}
              {duzenler && (
                <Button variant="ghost" size="icon" disabled={mesgul} title="Bağı kaldır"
                        onClick={() => bagla(t.id, false)} data-testid={`fix-menu-bag-kaldir-${t.id}`}>
                  <X size={14} />
                </Button>
              )}
            </div>
          ))}
          {duzenler && (
            <div className="flex gap-2 pt-2">
              <div className="flex-1 min-w-0">
                <SearchableSelect value={secim} onChange={setSecim} options={secenekler}
                                  placeholder="Ücretli tüketim seçin…" emptyText="Bağlanmamış ücretli tüketim yok"
                                  testId="fix-menu-tuketim-sec" />
              </div>
              <Button onClick={() => bagla(secim)} disabled={!secim || mesgul} data-testid="fix-menu-tuketim-bagla">
                <Link2 size={14} className="mr-2" /> Bağla
              </Button>
            </div>
          )}
        </Bolum>

        {menu.dis_kaporalar.length > 0 && (
          <Bolum baslik="Havale / elden kapora (POS'ta bilgi)">
            <p className="text-xs text-amber-700 mb-1">
              Bu paralar POS'tan ERP'ye kayıt olarak gelmez; banka ekstresinden ya da kasaya elle girilmelidir.
            </p>
            {menu.dis_kaporalar.map((k, i) => (
              <div key={k.id || i} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span>{gunAy(k.tarih)} · {k.yontem === 'havale' ? 'Havale/EFT' : 'Elden'}{k.aciklama ? ` · ${k.aciklama}` : ''}</span>
                <span>{trPara(k.tutar)}</span>
              </div>
            ))}
          </Bolum>
        )}

        {menu.adisyonlar.length > 0 && (
          <Bolum baslik="Adisyonlar">
            {menu.adisyonlar.map((a, i) => (
              <div key={`${a.bilet}-${i}`} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span>{gunAy(a.gun)} · #{a.bilet} · {a.tur === 'kapora' ? 'Kapora' : `${trSayi(a.adet)} kişi/adet`}</span>
                <span>{trPara(a.tutar)}</span>
              </div>
            ))}
          </Bolum>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kutu({ baslik, deger, alt, kirmizi }) {
  return (
    <div className="rounded-lg border p-2">
      <div className="text-xs text-[#6b6a65]">{baslik}</div>
      <div className={`font-semibold ${kirmizi ? 'text-red-600' : 'text-[#1a1916]'}`}>{deger}</div>
      {alt && <div className="text-[11px] text-[#6b6a65] leading-tight">{alt}</div>}
    </div>
  );
}

function Bolum({ baslik, children }) {
  return (
    <div>
      <div className="text-sm font-semibold text-[#1a1916] mb-1">{baslik}</div>
      {children}
    </div>
  );
}
